import type { Directory, SelectedFile } from '@witmetrics/utils';
import { mapToKey } from '@witmetrics/utils/arrays';
import { UPLOAD_STATUSES } from '@witmetrics/utils/constants';
import { isImage, isScan } from '@witmetrics/utils/files';
import { isEmpty } from '@witmetrics/utils/misc';
import { getSum } from '@witmetrics/utils/numbers';
import { excludeKey } from '@witmetrics/utils/objects';
import { generateUuid } from '@witmetrics/utils/uuid';
import { createPreviewImage } from '@/utils/images';
import { createScanPreview } from '@/utils/scans';
import type { StagedEntry } from './types';

const { IDLE } = UPLOAD_STATUSES;

export const classes = {
  dialog: 'w-[98vw] max-w-[560px] min-w-[360px]',
  dialogWide: 'max-w-[920px]',
  body: 'flex w-full max-h-[400px] min-h-[240px] flex-col gap-4 overflow-hidden pt-2 pb-4',
  hiddenInput: 'hidden',
  columns: 'flex min-h-0 flex-1 gap-5',
  inputColumn: 'flex w-full flex-col gap-2',
  inputColumnSplit: 'w-[496px] min-w-0 justify-center',
  dropCard:
    'place-center h-40 cursor-pointer flex-col border-2 border-dashed border-grey-4 dark:border-white/10 bg-gradient-to-br from-grey-1/20 from-[50%] to-grey-4/30 p-0 text-grey-8 dark:text-mauve-1 shadow-none transition-all hover:bg-grey-1 dark:hover:bg-white/[0.04]',
  dropCardActive:
    'border-blue-3 bg-grey-1 dark:bg-white/[0.06] from-blue-1/20 to-blue-2/30',
  dropContent: 'pointer-events-none flex flex-col items-center',
  dropIconWrapper:
    'upload-icon-wrapper mb-2 rounded-xl bg-white dark:bg-[#332e43] p-3 text-blue-3 shadow-lg transition-all',
  dropTitle: 'select-none text-inherit',
  dropSubtitle: 'select-none text-base font-light',
  addFilesWrapper: 'flex flex-col items-center gap-0.5',
  addFilesOr: 'text-sm text-grey-6 dark:text-mauve-3',
  addFilesLabel:
    'cursor-pointer text-sm font-medium text-blue-6 underline underline-offset-2 hover:text-blue-7',
  listColumn: 'flex min-h-0 min-w-0 flex-1 flex-col gap-1',
  listHeader:
    'place-between shrink-0 text-sm font-medium text-grey-7 dark:text-mauve-2',
  clearButton:
    'cursor-pointer text-sm font-medium text-blue-6 transition-colors hover:text-blue-7',
  list: 'flex min-h-0 flex-1 flex-col overflow-y-auto pr-1',
  progress: 'mt-1 shrink-0',
};

export const styles = {
  dropCard: {
    '&:hover .upload-icon-wrapper': {
      padding: '1rem',
    },
  },
};

export function countFiles(contents: Directory | File): number {
  if (contents instanceof File) return 1;
  return Object.keys(contents).reduce(
    (sum, key) => sum + countFiles(contents[key]),
    0
  );
}

export function stageDirectory(directory: Directory): StagedEntry[] {
  return Object.keys(directory).map((name) =>
    buildEntry(name, directory[name])
  );
}

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Returns the directory with the node at `path` removed, pruning any folder
 * that empties out as a result. (`as Directory` because Omit mangles the
 * index-signature type, though the runtime shape is unchanged.)
 */
export function removeAtPath(directory: Directory, path: string[]): Directory {
  const [head, ...rest] = path;
  if (isEmpty(rest)) return excludeKey(directory, head) as Directory;
  const child = directory[head];
  if (!child || child instanceof File) return directory;
  const pruned = removeAtPath(child, rest);
  return isEmpty(pruned)
    ? (excludeKey(directory, head) as Directory)
    : { ...directory, [head]: pruned };
}

export function stageFiles(files: File[]): StagedEntry[] {
  return files.map((file) => buildEntry(file.name, file));
}

function buildEntry(name: string, contents: Directory | File): StagedEntry {
  return {
    uuid: generateUuid(),
    name,
    contents,
    fileCount: countFiles(contents),
    isFolder: !(contents instanceof File),
  };
}

/** Merges the staged entries into a single tree keyed by name for upload. */
export function buildUploadTree(entries: StagedEntry[]): Directory {
  return Object.fromEntries(
    entries.map((entry): [string, Directory | File] => [
      entry.name,
      entry.contents,
    ])
  );
}

export function countStagedFiles(entries: StagedEntry[]): number {
  return getSum(...mapToKey(entries, 'fileCount'));
}

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Renders a thumbnail for previewable files (images + STL/PLY scans), mirroring
 * the case-creation flow. Returns an object URL, or undefined when the file
 * type isn't previewable — the row then falls back to its icon.
 */
export async function generateEntryPreview(
  file: File
): Promise<string | undefined> {
  let previewBlob: Blob | undefined;
  if (isImage(file.name)) {
    const source = URL.createObjectURL(file);
    try {
      previewBlob = await createPreviewImage(source);
    } finally {
      URL.revokeObjectURL(source);
    }
  } else if (isScan(file.name)) {
    previewBlob = await createScanPreview(file);
  }
  return previewBlob ? URL.createObjectURL(previewBlob) : undefined;
}

/** Releases the object URLs backing any generated previews. */
export function revokeEntryPreviews(entries: StagedEntry[]): void {
  entries.forEach((entry) => {
    if (entry.previewSource) URL.revokeObjectURL(entry.previewSource);
  });
}

// eslint-disable-next-line witmetrics/no-multiline-comments
/**
 * Wraps a staged File as a minimal SelectedFile for FilePreviewDialog — a
 * full-size object URL for the interactive viewer, no generated thumbnail.
 */
export function buildPreviewFile(file: File): SelectedFile {
  return {
    uuid: generateUuid(),
    uploadStatus: IDLE,
    file,
    source: URL.createObjectURL(file),
  };
}
